import React, { FunctionComponent, useEffect, useRef } from "react";
import { Link } from "gatsby";
import Image, { FluidObject } from "gatsby-image";
import { DiscussionEmbed } from "disqus-react";
import { Layout } from "../layout";
import { PostMeta } from "../postMeta";
import { Sticker } from "../sticker";
import { TableOfContents } from "../tableOfContents";
import { ProjectCardCompact } from "../projectCard";
import { SectionHeader } from "../sectionHeader";
import { PostGrid } from "../postCard";
import { PostType, TOPICS, TopicKey, topicStyle } from "../../topics";
import { PostSnippet, ProjectSummary } from "../../types";
import { formatDate, formatReadingTime, pluralize } from "../../data/format";
import avatar from "../../../static/logo.png";

const COPIED_RESET_MS = 1500;

interface BlogPost {
  title: string;
  description: string;
  slug: string;
  tags: string[];
  img: FluidObject;
  imgAlt?: string;
  publishedDate: Date;
  readingTime: number;
  topic: TopicKey;
  type: PostType;
  headings: { id: string; label: string }[];
  projects: ProjectSummary[];
  related: PostSnippet[];
  topicPostCount: number;
  html: string;
}

/** Wires the Copy buttons that gatsby-remark-maker renders into code blocks. */
const useCopyButtons = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = ref.current;
    if (!container) return undefined;
    const onClick = (event: MouseEvent) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "[data-copy-code]"
      );
      const code = button
        ?.closest(".code-block")
        ?.querySelector(".code-block__code");
      if (!button || !code) return;
      navigator.clipboard.writeText(code.textContent || "").then(() => {
        button.textContent = "Copied";
        setTimeout(() => {
          button.textContent = "Copy";
        }, COPIED_RESET_MS);
      });
    };
    container.addEventListener("click", onClick);
    return () => container.removeEventListener("click", onClick);
  }, []);
  return ref;
};

export const BlogPost: FunctionComponent<BlogPost> = ({
  title,
  description,
  slug,
  tags,
  img,
  imgAlt,
  publishedDate,
  readingTime,
  topic,
  type,
  headings,
  projects,
  related,
  topicPostCount,
  html,
}) => {
  const proseRef = useCopyButtons();
  const disqusConfig = {
    shortname: process.env.GATSBY_DISQUS_NAME as string,
    config: {
      identifier: slug,
      title,
    },
  };
  const [mainProject] = projects;
  const mainPart = mainProject?.parts.find((part) => part.href === slug);

  return (
    <Layout style={topicStyle(topic)}>
      <article>
        <header className="flex max-w-[960px] flex-col gap-5 px-gutter-sm pb-10 pt-14 md:px-gutter">
          <PostMeta topic={topic} longTopic type={type} />
          <h1 className="font-display text-post-title-sm font-extrabold md:text-post-title">
            {title}
          </h1>
          <p className="max-w-[760px] text-deck text-ink-soft">{description}</p>
          <div className="flex flex-wrap items-center gap-3.5 font-display text-ui">
            <img
              src={avatar}
              alt="Nahuel Taibo"
              className="h-10 w-10 rounded-full border-2 border-ink object-cover"
            />
            <span className="font-semibold">Nahuel Taibo</span>
            <span className="text-muted">
              {formatDate(publishedDate)} · {formatReadingTime(readingTime)}
            </span>
            {mainPart && (
              <Sticker rotate={2} className="ml-2 px-2.5 py-1 text-xs">
                Part {mainPart.part} of {mainProject.parts.length}
              </Sticker>
            )}
          </div>
        </header>

        <figure className="px-gutter-sm md:px-gutter">
          <div className="aspect-video border-2 border-ink md:aspect-auto md:h-[520px]">
            <Image
              fluid={img}
              alt={imgAlt || title}
              style={{ height: "100%" }}
            />
          </div>
          {imgAlt && (
            <figcaption className="pt-2.5 text-sm italic text-muted">
              {imgAlt}
            </figcaption>
          )}
        </figure>

        <div className="grid grid-cols-1 gap-12 px-gutter-sm py-14 md:px-gutter lg:grid-cols-post">
          <aside className="sticky top-6 hidden self-start lg:block">
            {headings.length > 0 && <TableOfContents items={headings} />}
          </aside>
          <div className="min-w-0 max-w-prose">
            <div
              ref={proseRef}
              className="prose-maker"
              dangerouslySetInnerHTML={{ __html: html }}
            />
            <div className="mt-14">
              <DiscussionEmbed {...disqusConfig} />
            </div>
          </div>
          <aside className="flex max-w-prose flex-col gap-4 self-start lg:sticky lg:top-6">
            {projects.map((project) => (
              <ProjectCardCompact
                key={project.slug}
                project={project}
                currentHref={slug}
              />
            ))}
            <div className="flex flex-wrap gap-2.5 font-display text-[13px] font-semibold text-ai">
              {tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/tags/${tag}`}
                  className="hover:text-spark"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </article>

      {related.length > 0 && (
        <section className="flex flex-col gap-6 px-gutter-sm pb-14 md:px-gutter">
          <SectionHeader
            topic={topic}
            title={`More from ${TOPICS[topic].shortName}`}
            link={{
              href: TOPICS[topic].href,
              label: `All ${pluralize(topicPostCount, "story", "stories")}`,
            }}
          />
          <PostGrid posts={related} compact />
        </section>
      )}
    </Layout>
  );
};
